import React, { Suspense, useContext, useEffect, useState, useRef } from "react";

import homePagePayload from "../../config/homePayload";
import homePageAPI from "../../api/homePageApi";
import { CategoryContext } from "../../context/CategoryContext";

import CouponFlash from "./CouponFlash";
import { Link, useHref } from "react-router-dom";
import { Row, Col, Container } from "react-bootstrap";

import {
  Swiper,
  SwiperSlide,
  Autoplay,
  Navigation,
  Pagination,
} from "./Shared/CustomSwiper";
import { FaBox, FaCheckCircle, FaHeadset } from "react-icons/fa";
import { ArrowRight, Leaf, Sparkles, Sprout } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Package, Flame, UtensilsCrossed } from "lucide-react";
import TopSellingCard from "../products/TopSellingCard";
import Badges from "../Badges";



const Product = React.lazy(() => import("../products/Card"));
const Testimonials = React.lazy(() => import("../testimonials/Card"));
const CategoryCard = React.lazy(() => import("../categories/Card"));
const Blog = React.lazy(() => import("../blogs/Card"));
const Banner = React.lazy(() => import("./Banner"));

function Home() {
  // Full category list (GET /categories, via CategoryContext) — same source
  // used in the Shop/Product filter sidebar. Home page only shows the first
  // 4 here; "View All" (below) links to /categories for the rest.
  const { allCategories } = useContext(CategoryContext);
  const categories = allCategories.slice(0, 4);

  const [coupons, setCoupons] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [products, setProducts] = useState([]);
  const [topSellerProducts, setTopSellerProducts] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [topBanners, setTopBanners] = useState([]);
  const [middleBanners, setMiddleBanners] = useState([]);
  const firstMiddleBanner = middleBanners[0];
  const secondMiddleBanner = middleBanners[1];
  const thirdMiddleBanner = middleBanners[1];
  const bannerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [parallaxOffset, setParallaxOffset] = useState(0);

  const [activeIdx, setActiveIdx] = useState(0);

  const parallaxRef = useRef(null);

  // Fallback if categories array is empty or undefined
  // if (!categories || categories.length === 0) return null;

  const active = categories[activeIdx] || categories[0];

  useEffect(() => {
    const handleScroll = () => {
      if (!parallaxRef.current) return;
      const rect = parallaxRef.current.getBoundingClientRect();
      const viewportCenter = window.innerHeight / 2;
      const elementCenter = rect.top + rect.height / 2;
      const distance = viewportCenter - elementCenter;
      setParallaxOffset(distance * 0.15); // 0.15 = parallax speed
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    fetchHomePageDetails();
  }, []);

  const CACHE_TIME = 3 * 60 * 1000;

  const applyData = (data) => {
    setTopBanners(data?.banners?.filter((b) => b.position === "top"));
    setMiddleBanners(data?.banners?.filter((b) => b.position === "middle"));
    setCoupons(data?.coupons);
    setTestimonials(data?.testimonials);
    setBlogs(data?.blogs);
    setProducts(data?.products);
    setTopSellerProducts(data?.topSellerProducts);
  };

  const fetchHomePageDetails = async () => {
    try {
      const cached = sessionStorage.getItem("homeData");
      const parsed = cached ? JSON.parse(cached) : null;

      if (parsed) applyData(parsed.data);

      if (!parsed || Date.now() - parsed.timestamp > CACHE_TIME) {
        const res = await homePageAPI.homePage(homePagePayload);
        if (res.code === 200) {
          sessionStorage.setItem(
            "homeData",
            JSON.stringify({ data: res.data, timestamp: Date.now() }),
          );
          if (!parsed) applyData(res.data); // only update state if no cache was shown
        }
      }
    } catch (error) {
      console.error("Home API Error:", error);
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // This allows the animation to reset and run again every time it enters view
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.2 } // Runs when 20% of the banner is visible
    );

    if (bannerRef.current) {
      observer.observe(bannerRef.current);
    }

    return () => {
      if (bannerRef.current) observer.unobserve(bannerRef.current);
    };
  }, []);

  const prevRef = useRef(null);
  const nextRef = useRef(null);





  const imgVariants = {
    enter: { clipPath: "inset(100% 0 0 0)", scale: 1.06 },
    center: { clipPath: "inset(0% 0 0 0)", scale: 1, transition: { duration: 0.75, ease: [0.76, 0, 0.24, 1] } },
    exit: { clipPath: "inset(0 0 100% 0)", scale: 0.97, transition: { duration: 0.5, ease: [0.76, 0, 0.24, 1] } },
  };



  return (
    <>
      {topBanners && topBanners.length > 0 && (
        <div>
          <Banner item={topBanners} />
        </div>
      )}


      {coupons && coupons.length > 0 && (
        <section className="w-full bg-[var(--color-bg-alt)] border-y border-[var(--color-border)] py-3 overflow-hidden shadow-sm">
          <div className="relative flex w-full overflow-hidden [mask-image:_linear-gradient(to_right,transparent_0,_black_64px,_black_calc(100%-64px),transparent_100%)]">
            {/* Container holding both sets */}
            <div className="flex w-max animate-marquee hover:[animation-play-state:paused]">
              {/* Original Set */}
              <div className="flex items-center gap-6 pr-6 shrink-0">
                {coupons.map((coupon, index) => (
                  <CouponFlash key={coupon._id || index} item={coupon} />
                ))}
              </div>

              {/* Duplicate Set */}
              <div className="flex items-center gap-6 pr-6 shrink-0" aria-hidden="true">
                {coupons.map((coupon, index) => (
                  <CouponFlash
                    key={`duplicate-${coupon._id || index}`}
                    item={coupon}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}







      {products && products.length > 0 && (
        <section
          className="relative py-10 md:py-12 overflow-hidden"
          style={{
            backgroundColor: "var(--color-bg)",
          }}
        >
          {/* Background Decoration */}
          <div
            className="absolute top-0 left-0 w-full h-full pointer-events-none"
          />

          <div className="container position-relative px-4 sm:px-6">
            {/* Heading */}
            <div className="flex flex-col gap-6 mb-8 md:mb-12">

              {/* Heading + Button */}
              <div className="flex items-center justify-between sm:items-end sm:flex-row">

                <div className="flex-1">
                  {/* Badge - Hide on mobile */}
                  <span
                    className="hidden sm:inline-block px-3 py-1 rounded-pill mb-3"
                    style={{
                      background: "rgba(176,141,87,.12)",
                      color: "var(--color-accent-gold)",
                      fontFamily: "var(--font-body)",
                      fontSize: "var(--font-size-xs)",
                      letterSpacing: ".18em",
                      textTransform: "uppercase",
                      fontWeight: "var(--font-weight-bold)",
                    }}
                  >
                    ✦ Featured Collection
                  </span>

                  <h2
                    className="mb-0 sm:mb-2"
                    style={{
                      fontFamily: "var(--font-heading)",
                      fontSize: "clamp(1.6rem, 5vw, 3.4rem)",
                      color: "var(--color-text)",
                      fontWeight: "var(--font-weight-bold)",
                      lineHeight: "var(--line-height-tight)",
                    }}
                  >
                    Products
                  </h2>

                  {/* Hide text on mobile */}
                  {/* <p
                    className="hidden sm:block mb-0 max-w-[550px]"
                    style={{
                      fontFamily: "var(--font-body)",
                      color: "var(--color-text-muted)",
                      fontSize: "var(--font-size-base)",
                    }}
                  >
                    Discover our most loved premium , carefully sourced for
                    authentic flavour and everyday cooking.
                  </p> */}
                </div>

                <div className="ml-4 flex-shrink-0">
                  <Link
                    to="/shop"
                    className="inline-flex items-center justify-center gap-2 px-4 sm:px-5 py-2.5 sm:py-3 rounded-full text-decoration-none transition-all text-center"
                    style={{
                      background: "var(--color-primary)",
                      color: "var(--color-white)",
                      fontFamily: "var(--font-body)",
                      letterSpacing: ".12em",
                      textTransform: "uppercase",
                      fontSize: "var(--font-size-xs)",
                      fontWeight: "var(--font-weight-bold)",
                      boxShadow: "0 10px 25px rgba(166,71,46,0.18)",
                      transition: "background var(--transition-normal)",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "var(--color-primary-dark)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "var(--color-primary)")
                    }
                  >
                    View All →
                  </Link>
                </div>

              </div>

            </div>

            {/* Products */}
            <Row className="g-3 sm:g-4">
              <Suspense fallback={<div></div>}>
                {products.map((product, index) => (
                  <Col
                    lg={3}
                    md={4}
                    sm={6}
                    xs={6}
                    key={product._id || index}
                    className="mb-3 sm:mb-4 px-2 sm:px-3"
                  >
                    <Product item={product} />
                  </Col>
                ))}
              </Suspense>
            </Row>
          </div>
        </section>
      )}


      {categories && categories.length > 0 && (
        <>
          <section className="relative w-full overflow-hidden">
            <div className="container mx-auto px-4 sm:px-6 lg:px-16 pt-8 lg:pt-16 pb-6 lg:pb-10">
              <div className="flex items-end justify-between gap-4">

                {/* Left Content */}
                <div className="flex-1 min-w-0">
                  <p className="mb-2 text-[10px] sm:text-xs uppercase tracking-[0.12em] text-[var(--color-primary)] font-body font-medium">
                    Explore Categories
                  </p>

                  <h2 className="font-heading text-[1.6rem] sm:text-[2.2rem] md:text-[3rem] lg:text-[clamp(2rem,4vw,3.8rem)] font-bold leading-tight tracking-[-0.02em] text-[var(--color-text)]">
                    The Collection
                  </h2>
                </div>

                {/* Right Content */}
                <div className="flex flex-col items-end gap-3 flex-shrink-0">

                  {/* Counter */}
                  <div className="hidden lg:flex items-center gap-2">
                    <span
                      className="section-eyebrow"
                      style={{ color: "var(--color-text-muted)" }}
                    >
                      {String(activeIdx + 1).padStart(2, "0")}
                    </span>

                    <div className="w-[42px] h-px bg-[var(--color-border)]" />

                    <span
                      className="section-eyebrow"
                      style={{ color: "var(--color-text-muted)" }}
                    >
                      {String(categories.length).padStart(2, "0")}
                    </span>
                  </div>

                  {/* View All Button */}
                  <Link
                    to="/categories"
                    className="custom-link-category group inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-full font-medium text-xs sm:text-sm tracking-wide transition-all duration-300 hover:-translate-y-1 whitespace-nowrap"
                    style={{
                      background: "var(--color-primary)",
                      color: "var(--color-white)",
                      boxShadow: "var(--shadow-md)",
                    }}
                  >
                    <span>View All</span>

                    <svg
                      className="transition-transform duration-300 group-hover:translate-x-1"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M5 12h14" />
                      <path d="M13 5l7 7-7 7" />
                    </svg>
                  </Link>

                </div>
              </div>
            </div>

            {/* ─── MOBILE LAYOUT (Reduced Spacing) ─── */}
            {/* ─── MOBILE LAYOUT ─── */}
            <div className="lg:hidden px-4 py-4">
              <div className="mobile-scroll flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory">
                {categories.map((cat, index) => {
                  const catBg = cat.colorBg || "var(--color-bg)";

                  return (
                    <Link
                      key={cat._id || index}
                      to={`/categories/${cat?.slug || cat?._id}`}
                      className="mobile-card snap-center text-decoration-none flex flex-col overflow-hidden"
                      style={{
                        width: "75vw",
                        background: catBg,
                        border: "1px solid var(--color-border)",
                        borderRadius: "var(--radius-md)",
                      }}
                    >
                      {/* Image */}
                      <img
                        src={cat.image}
                        alt={cat.name}
                        style={{
                          width: "100%",
                          aspectRatio: "4 / 5",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />

                      {/* Content */}
                      <div
                        style={{
                          padding: "12px",
                          background: catBg,
                        }}
                      >
                        {cat.tagline && (
                          <span
                            className="cat-tag"
                            style={{
                              background: "var(--color-tan-light)",
                              color: "var(--color-primary-dark)",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              marginBottom: 6,
                              padding: "2px 8px",
                              borderRadius: "var(--radius-sm)",
                              fontSize: "0.72rem",
                            }}
                          >
                            <span
                              style={{
                                width: 5,
                                height: 5,
                                borderRadius: "50%",
                                background: "var(--color-primary)",
                              }}
                            />
                            {cat.tagline}
                          </span>
                        )}

                        <h3
                          style={{
                            fontFamily: "var(--font-heading)",
                            fontSize: "1.25rem",
                            lineHeight: 1.2,
                            color: "var(--color-text)",
                            marginBottom: 6,
                          }}
                        >
                          {cat.name}
                        </h3>

                        <p
                          style={{
                            color: "var(--color-text-muted)",
                            fontSize: "0.82rem",
                            lineHeight: 1.5,
                            margin: 0,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {cat.description ||
                            "Explore our premium collection curated specially for you."}
                        </p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>

            <div className="hidden lg:grid grid-cols-12 min-h-[85vh] px-16 pb-20 gap-12 items-start">

              {/* LEFT: Sticky Image Panel */}
              <div className="col-span-5 sticky top-24 pt-4">
                <div style={{ position: "relative" }}>

                  {/* Ghost script behind image */}
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={(active._id || active.name) + "-script-bg"}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                      style={{
                        position: "absolute",
                        top: -30,
                        left: -10,
                        fontFamily: "var(--font-heading)",
                        fontSize: "5rem",
                        color: "transparent",
                        WebkitTextStroke: "1px var(--color-border)",
                        pointerEvents: "none",
                        zIndex: 0,
                        lineHeight: 1,
                      }}
                    >
                      {active.script || "Collection"}
                    </motion.div>
                  </AnimatePresence>

                  {/* Image frame */}
                  <div className="img-frame" style={{ maxWidth: 380, margin: "0 auto", position: "relative", zIndex: 1, borderRadius: "var(--radius-md)", overflow: "hidden", boxShadow: "var(--shadow-lg)" }}>
                    {active && (
                      <AnimatePresence initial={false} mode="wait">
                        <div style={{ width: 380, height: 520, border: "2px solid var(--color-accent-gold)" }}>
                          <img
                            src={active?.image}
                            alt={active?.name}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        </div>
                      </AnimatePresence>
                    )}

                    {/* Bottom glass card */}
                    <div style={{
                      position: "absolute", bottom: 20, left: 16, right: 16,
                      background: "rgba(46, 33, 26, 0.75)",
                      backdropFilter: "blur(16px)",
                      border: "1px solid rgba(201, 162, 109, 0.3)",
                      borderRadius: "var(--radius-sm)",
                      padding: "12px 16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      zIndex: 10,
                    }}>
                      <div>
                        <p style={{ fontFamily: "var(--font-body)", fontSize: "0.55rem", letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--color-tan-light)", marginBottom: 2 }}>
                          Origin
                        </p>
                        <p style={{ fontFamily: "var(--font-heading)", fontSize: "0.95rem", color: "var(--color-white)", fontWeight: 600 }}>
                          {active.origin || "India"}
                        </p>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <p style={{ fontFamily: "var(--font-body)", fontSize: "0.55rem", letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--color-tan-light)", marginBottom: 2 }}>
                          Range
                        </p>
                        <p style={{ fontFamily: "var(--font-heading)", fontSize: "0.95rem", color: "var(--color-white)", fontWeight: 600 }}>
                          {active.count || "Available"}
                        </p>
                      </div>
                    </div>

                    {/* Corner accent */}
                    <div style={{ position: "absolute", top: 16, right: 16, width: 32, height: 32, borderTop: "2px solid var(--color-accent-gold)", borderRight: "2px solid var(--color-accent-gold)", zIndex: 10 }} />
                  </div>

                  {/* Orbit ring */}
                  {/* <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 22, repeat: Infinity, ease: "linear" }}
                    style={{
                      position: "absolute",
                      width: 420, height: 420,
                      borderRadius: "50%",
                      border: "1px dashed var(--color-accent-gold)",
                      top: "50%", left: "50%",
                      transform: "translate(-50%, -50%)",
                      pointerEvents: "none",
                      zIndex: 0,
                    }}
                  /> */}
                </div>
              </div>

              {/* RIGHT: Category List */}
              <div className="col-span-7 pt-4 flex flex-col justify-center">
                <div>
                  {categories.map((cat, i) => {
                    const catColor = cat.color || "var(--color-primary)";
                    const Icon = cat.icon || Package;
                    const isActive = activeIdx === i;

                    return (
                      <div
                        key={cat._id || i}
                        className={`cat-row ${isActive ? "is-active" : ""}`}
                        style={{ "--row-color": catColor, borderBottom: "1px solid var(--color-border)", padding: "20px 0", cursor: "pointer", position: "relative" }}
                        onMouseEnter={() => setActiveIdx(i)}
                        onClick={() => setActiveIdx(i)}
                      >
                        {/* Active left bar */}
                        {isActive && (
                          <motion.div
                            layoutId="active-bar"
                            style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: "var(--color-primary)", borderRadius: "0 var(--radius-sm) var(--radius-sm) 0" }}
                          />
                        )}

                        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", paddingLeft: 20, position: "relative", zIndex: 1 }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: "flex", alignItems: "baseline", gap: 16, marginBottom: 4 }}>
                              <span className="cat-num" style={{ color: isActive ? "var(--color-primary)" : "var(--color-text-muted)", fontFamily: "var(--font-heading)", fontSize: "1.2rem" }}>
                                {cat.id || `0${i + 1}`}
                              </span>
                              {cat.script && <span className="cat-script" style={{ color: "var(--color-accent-green)", fontFamily: "var(--font-body)" }}>{cat.script}</span>}
                            </div>

                            <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                              <h3
                                className="cat-title"
                                style={{
                                  fontFamily: "var(--font-heading)",
                                  fontSize: "2rem",
                                  color: isActive ? "var(--color-primary)" : "var(--color-text)",
                                  transform: isActive ? "translateX(6px)" : "none",
                                  transition: "transform 0.3s ease, color 0.3s ease"
                                }}
                              >
                                {cat.name}
                              </h3>

                              {/* Tagline chip */}
                              {cat.tagline && (
                                <span className="cat-tag" style={{ background: "var(--color-tan-light)", color: "var(--color-primary-dark)", flexShrink: 0, padding: "4px 10px", borderRadius: "var(--radius-full)", fontSize: "var(--font-size-xs)", fontFamily: "var(--font-body)" }}>
                                  {cat.tagline}
                                </span>
                              )}
                            </div>

                            {/* Expanded description */}
                            <motion.div
                              initial={false}
                              animate={{ height: isActive ? "auto" : 0, opacity: isActive ? 1 : 0 }}
                              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                              style={{ overflow: "hidden" }}
                            >
                              <p className="cat-desc" style={{ marginTop: 12, paddingLeft: 32, color: "var(--color-text-muted)", fontFamily: "var(--font-body)", lineHeight: "var(--line-height-normal)" }}>
                                {cat.description || "Explore our high quality products specially made for you."}
                              </p>
                              <div style={{ marginTop: 18, paddingLeft: 32, display: "flex", gap: 16, alignItems: "center" }}>
                                <Link
                                  to={`/categories/${cat?.slug || cat?._id}`}
                                  style={{
                                    display: "inline-flex", alignItems: "center", gap: 8,
                                    padding: "10px 22px",
                                    background: "var(--color-primary)",
                                    color: "var(--color-white)",
                                    textDecoration: "none",
                                    borderRadius: "var(--radius-sm)",
                                    fontFamily: "var(--font-body)",
                                    fontSize: "var(--font-size-xs)",
                                    fontWeight: 600,
                                    letterSpacing: "0.18em",
                                    textTransform: "uppercase",
                                    boxShadow: "var(--shadow-md)",
                                    transition: "transform 0.2s ease, background 0.2s ease",
                                  }}
                                >
                                  Shop {cat.name}
                                  <ArrowUpRight size={12} />
                                </Link>
                                {cat.count && (
                                  <span style={{ fontFamily: "var(--font-body)", fontSize: "var(--font-size-xs)", letterSpacing: "0.15em", textTransform: "uppercase", color: "var(--color-text-muted)" }}>
                                    {cat.count}
                                  </span>
                                )}
                              </div>
                            </motion.div>
                          </div>

                          {/* Right: Icon */}
                          <motion.div
                            animate={{ scale: isActive ? 1 : 0.7, opacity: isActive ? 1 : 0.4 }}
                            transition={{ duration: 0.3 }}
                            style={{
                              width: 48, height: 48,
                              borderRadius: "50%",
                              background: isActive ? "var(--color-primary)" : "var(--color-bg)",
                              border: "1px solid var(--color-border)",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              color: isActive ? "var(--color-white)" : "var(--color-text-muted)",
                              flexShrink: 0,
                              marginTop: 4,
                            }}
                          >
                            <Icon size={20} />
                          </motion.div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>
        </>
      )}






      {topSellerProducts && topSellerProducts.length > 0 && (
        <section className="relative py-10 md:py-12 overflow-hidden min-h-screen"
          style={{
            background: "var(--color-bg-alt)",
          }}
        >
          {/* --- DECORATIVE BACKGROUND LINES --- */}
          <div className="absolute inset-0 pointer-events-none z-0 flex justify-around opacity-[0.03]">
            <div className="w-[1px] h-full bg-black"></div>
            <div className="w-[1px] h-full bg-black"></div>
            <div className="w-[1px] h-full bg-black"></div>
            <div className="w-[1px] h-full bg-black"></div>
            <div className="w-[1px] h-full bg-black hidden md:block"></div>
          </div>

          <div className="container mx-auto px-6 relative z-10">
            {/* --- HEADER --- */}
            <div className="flex flex-col gap-6 mb-8 md:mb-12">

              {/* Heading + Button */}
              <div className="flex items-center justify-between sm:items-end sm:flex-row">

                <div className="flex-1">
                  {/* Badge - Hide on mobile */}
                  <h2
                    className="mb-0 sm:mb-2"
                    style={{
                      fontFamily: "var(--font-heading)",
                      fontSize: "clamp(1.6rem, 5vw, 3.4rem)",
                      color: "var(--color-text)",
                      fontWeight: "var(--font-weight-bold)",
                      lineHeight: "var(--line-height-tight)",
                    }}
                  >
                    Best Seller
                  </h2>
                </div>

                <div className="ml-4 flex-shrink-0">
                  <Link
                    to="/shop"
                    className="inline-flex items-center justify-center gap-2 px-4 sm:px-5 py-2.5 sm:py-3 rounded-full text-decoration-none transition-all text-center"
                    style={{
                      background: "var(--color-primary)",
                      color: "var(--color-white)",
                      fontFamily: "var(--font-body)",
                      letterSpacing: ".12em",
                      textTransform: "uppercase",
                      fontSize: "var(--font-size-xs)",
                      fontWeight: "var(--font-weight-bold)",
                      boxShadow: "0 10px 25px rgba(166,71,46,0.18)",
                      transition: "background var(--transition-normal)",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "var(--color-primary-dark)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "var(--color-primary)")
                    }
                  >
                    View All →
                  </Link>
                </div>

              </div>

            </div>
            {/* --- STAGGERED GRID LAYOUT --- */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-8">
              {topSellerProducts?.map((product, index) => (
                <motion.div
                  key={product?._id}
                  // Stagger Effect: Even items (0, 2) start normal, Odd items (1, 3) are pushed down on desktop
                  className={`${index % 2 !== 0 ? "lg:mt-24" : "lg:mt-0"
                    } flex justify-center`}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{
                    delay: index * 0.2,
                    duration: 0.8,
                    ease: "easeOut",
                  }}
                >
                  <TopSellingCard item={product} />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {thirdMiddleBanner && (
        <section
          className="relative w-full py-16 md:py-24 overflow-hidden flex items-center justify-center text-center"
          style={{
            backgroundImage: `url(${thirdMiddleBanner.image})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat"
          }}
        >

          {/* Brand-Colored Soft Overlay (Lightened) */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: "var(--color-secondary)",
              opacity: 0.4
            }}
          />

          {/* Content Container */}
          <div className="container mx-auto px-4 md:px-8 relative z-10">
            <div className="max-w-3xl mx-auto flex flex-col items-center">

              {/* Spotlight Tagline Chip */}
              <span
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-[0.2em] mb-4 shadow-sm"
                style={{
                  background: "var(--color-tan-light)",
                  color: "var(--color-primary-dark)",
                  border: "1px solid var(--color-border)"
                }}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--color-primary)" }} />
                Traditional Attire
              </span>

              {/* Heading */}
              <h2
                className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-3 drop-shadow-md"
                style={{
                  fontFamily: "var(--font-heading)",
                  color: "var(--color-white)",
                  lineHeight: 1.1
                }}
              >
                {thirdMiddleBanner.title}
              </h2>

              {/* Subtitle / Description */}
              <p
                className="text-sm sm:text-base mb-6 font-normal leading-relaxed max-w-xl text-center"
                style={{
                  fontFamily: "var(--font-body)",
                  color: "white"
                }}
              >
                {thirdMiddleBanner.subTitle}
              </p>

              {/* Discover More Button */}
              <Link
                to="/shop"
                className="  custom-text-banner-link  inline-flex items-center justify-center px-8 py-3.5 text-xs sm:text-sm font-bold uppercase tracking-[0.15em] rounded-full transition-all duration-300 hover:-translate-y-1 shadow-xl"
                style={{
                  background: "var(--color-primary)",
                  color: "var(--color-white)",
                  fontFamily: "var(--font-body)"
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = "var(--color-primary-dark)"}
                onMouseLeave={(e) => e.currentTarget.style.background = "var(--color-primary)"}
              >
                Discover More →
              </Link>

            </div>
          </div>
        </section>
      )}

      {testimonials && testimonials.length > 0 && (
        <section className="testimonials-section py-10 md:py-12">
          <Container>
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-2">
              <div className="w-full sm:w-auto">
                <h2
                  className="mb-0"
                  style={{
                    fontFamily: "var(--font-heading)",
                    fontSize: "clamp(2rem, 4vw, 3.4rem)",
                    color: "var(--color-text)",
                    fontWeight: "var(--font-weight-bold)",
                    lineHeight: "var(--line-height-tight)"
                  }}
                >
                  Testimonial
                </h2>
              </div>
            </div>

            <Swiper
              modules={[Autoplay]}
              autoplay={{ delay: 350000, disableOnInteraction: false }}
              loop={true}
              spaceBetween={20}
              breakpoints={{
                0: { slidesPerView: 1.1 },
                576: { slidesPerView: 1.5 },
                768: { slidesPerView: 2 },
                1200: { slidesPerView: 3 },
              }}
            >
              <Suspense fallback={<div>Loading...</div>}>
                {testimonials.map((testimonial, index) => (
                  <SwiperSlide key={index}>
                    <Testimonials item={testimonial} />
                  </SwiperSlide>
                ))}
              </Suspense>
            </Swiper>
          </Container>
        </section>
      )}




      {firstMiddleBanner && (
        <section
          className="relative py-10 md:py-12 overflow-hidden"
          ref={bannerRef}
          style={{
            background: "var(--color-bg-alt)",
          }}
        >
          {/* Matching Container Padding & Alignment */}
          <div className="container position-relative px-4 sm:px-6">
            <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-12">

              {/* Left Image Column */}
              <div className="lg:col-span-6 relative">
                <div className="relative mx-auto w-full max-w-lg lg:max-w-none">

                  {/* Main Image Container */}
                  <div className="relative overflow-hidden  border border-black/10 bg-gray-100 shadow-md">
                    <img
                      src={firstMiddleBanner.image}
                      alt={firstMiddleBanner.title}
                      className="h-[320px] sm:h-[420px] lg:h-[460px] w-full object-cover transition-transform duration-700 ease-out hover:scale-105"
                    />
                  </div>
                </div>
              </div>

              {/* Right Content Column */}
              <div className="lg:col-span-6 flex flex-col items-start space-y-4 sm:space-y-5">

                {/* Badge Tag */}
                <span
                  className="inline-block px-3 py-1 rounded-full"
                  style={{
                    background: "rgba(176,141,87,.12)",
                    color: "var(--color-accent-gold)",
                    fontFamily: "var(--font-body)",
                    fontSize: "var(--font-size-xs)",
                    letterSpacing: ".18em",
                    textTransform: "uppercase",
                    fontWeight: "var(--font-weight-bold)",
                  }}
                >
                  ✦ Timeless Elegance
                </span>

                {/* Banner Title */}
                <h2
                  className="mb-0"
                  style={{
                    fontFamily: "var(--font-heading)",
                    fontSize: "clamp(1.8rem, 4vw, 3rem)",
                    color: "var(--color-text)",
                    fontWeight: "var(--font-weight-bold)",
                    lineHeight: "var(--line-height-tight)",
                  }}
                >
                  {firstMiddleBanner.title}
                </h2>

                {/* Banner Subtitle */}
                <p
                  className="mb-0 max-w-[550px]"
                  style={{
                    fontFamily: "var(--font-body)",
                    color: "var(--color-text-muted)",
                    fontSize: "var(--font-size-base)",
                    lineHeight: "1.6",
                  }}
                >
                  {firstMiddleBanner.subTitle}
                </p>

                {/* Decorative Divider */}
                <div
                  className="h-0.5 w-16 rounded-full my-1"
                  style={{ background: "var(--color-accent-gold)" }}
                />

                {/* Action Button */}
                <div className="pt-2">
                  <Link
                    to="/shop"
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-decoration-none transition-all text-center"
                    style={{
                      background: "var(--color-primary)",
                      color: "var(--color-white)",
                      fontFamily: "var(--font-body)",
                      letterSpacing: ".12em",
                      textTransform: "uppercase",
                      fontSize: "var(--font-size-xs)",
                      fontWeight: "var(--font-weight-bold)",
                      boxShadow: "0 10px 25px rgba(166,71,46,0.18)",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "var(--color-primary-dark)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "var(--color-primary)")
                    }
                  >
                    <span>Explore Collection</span>
                    <span className="text-base">→</span>
                  </Link>
                </div>

              </div>


            </div>
          </div>
        </section>
      )}

      {blogs && blogs.length > 0 && (
        <section className="py-10 md:py-12  text-[#2E211A] overflow-hidden">
          <div className="container position-relative px-4 sm:px-6">
            {/* Section Header */}
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-8 md:mb-12">
              <div className="w-full sm:w-auto">

                {/* Mobile: Heading + Button */}
                <div className="flex items-center justify-between sm:block">
                  <h2
                    className="mb-0 sm:mb-2"
                    style={{
                      fontFamily: "var(--font-heading)",
                      fontSize: "clamp(2rem, 4vw, 3.4rem)",
                      color: "var(--color-text)",
                      fontWeight: "var(--font-weight-bold)",
                      lineHeight: "var(--line-height-tight)",
                    }}
                  >
                    Blog
                  </h2>

                  {/* Mobile Button */}
                  <div className="sm:hidden">
                    <Link
                      to="/blog"
                      className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-decoration-none transition-all"
                      style={{
                        background: "var(--color-primary)",
                        color: "var(--color-white)",
                        fontFamily: "var(--font-body)",
                        letterSpacing: ".12em",
                        textTransform: "uppercase",
                        fontSize: "var(--font-size-xs)",
                        fontWeight: "var(--font-weight-bold)",
                        boxShadow: "0 10px 25px rgba(166,71,46,0.18)",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "var(--color-primary-dark)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "var(--color-primary)")
                      }
                    >
                      View All →
                    </Link>
                  </div>
                </div>
              </div>

              {/* Desktop Button */}
              <div className="hidden sm:flex justify-end">
                <Link
                  to="/blog"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-decoration-none transition-all"
                  style={{
                    background: "var(--color-primary)",
                    color: "var(--color-white)",
                    fontFamily: "var(--font-body)",
                    letterSpacing: ".12em",
                    textTransform: "uppercase",
                    fontSize: "var(--font-size-xs)",
                    fontWeight: "var(--font-weight-bold)",
                    boxShadow: "0 10px 25px rgba(166,71,46,0.18)",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "var(--color-primary-dark)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "var(--color-primary)")
                  }
                >
                  View All →
                </Link>
              </div>
            </div>

            {/* Swiper Slider */}
            <div className="relative -mx-4 px-4 sm:mx-0 sm:px-0">
              <Swiper
                modules={[Pagination, Autoplay]}
                autoplay={{
                  delay: 3500,
                  disableOnInteraction: false,
                }}
                loop={true}
                spaceBetween={24}
                pagination={{
                  clickable: true,
                  dynamicBullets: true,
                }}
                breakpoints={{
                  0: {
                    slidesPerView: 1.1,
                  },
                  576: {
                    slidesPerView: 1.5,
                  },
                  768: {
                    slidesPerView: 2,
                  },
                  1200: {
                    slidesPerView: 3,
                  },
                }}
                className="pb-12 pt-2 !px-1"
              >
                <Suspense
                  fallback={
                    <div className="h-64 flex items-center justify-center text-[#6B5D53]">
                      Loading stories...
                    </div>
                  }
                >
                  {blogs.map((blog) => (
                    <SwiperSlide key={blog.id} className="h-auto">
                      <Blog item={blog} />
                    </SwiperSlide>
                  ))}
                </Suspense>
              </Swiper>
            </div>
          </div>
        </section>
      )}

      <Badges />
    </>
  );
}

export default Home;